import * as React from 'react';
import {
  IQuadrantReportState,
  quadrantReportProvider,
} from '../../../data/providers/reports/quadrantReportProvider';
import { Entity, EntityType } from '../../../data/types';
import { Column } from '../../layout/Column';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { EPdfMode, Pdf, isPdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { ErrorMessage } from '../utils/ErrorMessages';
import { ReportOverview } from '../utils/ReportOverview';
import { quartileColors } from '../utils/quartileColors';
import { useFilename } from '../utils/useFilename';
import { QuadrantGraphCard } from './QuadrantGraphCard';
import { ResultList } from './ResultList';
import { QuadrantSettings } from './settings/Settings';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const QuadrantReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] = React.useState<IQuadrantReportState>();
  /**
   * @description subscribe to data provider
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = quadrantReportProvider(
      props.entityType,
      props.entity
    ).subscribe((state: IQuadrantReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename(
    'quadrant',
    props.entity,
    reportState?.timeframe
  );

  return (
    <React.Fragment>
      {reportState?.isValid && !props.loading ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={() => {}}
              filename={filename}
              title='Quadrant Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
            >
              <Column>
                <ReportOverview
                  populationTimeframe={{
                    dateType: reportState.comparisonPopulation?.dateRangeType,
                    dateRange:
                      reportState.comparisonPopulation?.dateRangeValues,
                  }}
                  populationEntityName={
                    reportState.comparisonPopulation?.entity?.label
                  }
                  targetTimeframe={{
                    dateType: reportState?.targetTimeframe?.dateType,
                    dateRange: reportState?.targetTimeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName={`Quadrant Report - ${reportState?.settings?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <QuadrantGraphCard
                  entity={props.entity}
                  reportState={reportState}
                  pdfMode={props.pdfMode}
                  quartileColors={quartileColors}
                />
                <ResultList
                  reportState={reportState}
                  colors={quartileColors}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </Column>
            </Pdf>
          </PdfContainer>
        ) : (
          <Column>
            {!props.loading ? (
              <React.Fragment>
                <ReportOverview
                  populationTimeframe={{
                    dateType: reportState.comparisonPopulation?.dateRangeType,
                    dateRange:
                      reportState.comparisonPopulation?.dateRangeValues,
                  }}
                  populationEntityName={
                    reportState.comparisonPopulation?.entity?.label
                  }
                  targetTimeframe={{
                    dateType: reportState?.targetTimeframe?.dateType,
                    dateRange: reportState?.targetTimeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName={`Quadrant Report - ${reportState?.settings?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <QuadrantGraphCard
                  entity={props.entity}
                  reportState={reportState}
                  pdfMode={props.pdfMode}
                  quartileColors={quartileColors}
                />
                <ResultList
                  reportState={reportState}
                  colors={quartileColors}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </React.Fragment>
            ) : (
              <LinearLoading />
            )}
          </Column>
        )
      ) : props.loading ? (
        <LinearLoading />
      ) : (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
        />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
}): React.ReactElement => {
  const onSave = (): void => setLoading(true);
  switch (errorCode) {
    case 'missing-template':
      return (
        <ErrorMessage iconName='window' text='Please Select a Template'>
          <QuadrantSettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    case 'missing-axis-settings':
      return (
        <ErrorMessage iconName='poll' text='Configure Axis Settings'>
          <div style={{ marginTop: '10px' }}>
            Please select your desired metrics for each axis in settings.
          </div>
          <QuadrantSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'invalid-date-range':
      return (
        <ErrorMessage iconName='date_range' text='Missing Date Range'>
          <div style={{ marginTop: '10px' }}>
            Please select a date range for this report.
          </div>
          <QuadrantSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            There are not enough data points to produce this report. Try
            changing the timeframe in settings
          </div>
          <QuadrantSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    default:
      return null;
  }
};
